import $ from "jquery"
import htmlTemplate from "./playerTemplate.html"

type PlaybackFinishedCallback = () => void

type PlayerProperties = {
    container:JQuery,
    beepSource:string
}

export default class Player {

    private container:JQuery
    private audioPlayer:JQuery
    private beepPlayer:JQuery
    private audioPlayerElement:HTMLAudioElement
    private beepPlayerElement:HTMLAudioElement

    private audioPlaybackPromise:Promise<void>|null
    private beepPlaybackPromise:Promise<void>|null

    private muted:boolean = false
    private shouldMask:boolean = false

    private controlPlay:JQuery
    private controlVol:JQuery
    private controlVolSlider:JQuery
    private controlProgress:JQuery
    private displayPosition:JQuery
    private displayDuration:JQuery
    private controlsVolume:JQuery

    constructor(properties:PlayerProperties) {
        this.container = properties.container

        const playerNode = $(htmlTemplate)
        this.container.append(playerNode)
        
        this.audioPlayer = $("<audio></audio>")
        this.audioPlayerElement = this.audioPlayer[0] as HTMLAudioElement
        this.audioPlayerElement.preload = "auto"
        this.audioPlayerElement.muted = this.muted

        this.beepPlayer = $("<audio></audio>")
        this.beepPlayerElement = this.beepPlayer[0] as HTMLAudioElement
        this.beepPlayerElement.src =  properties.beepSource
        this.beepPlayerElement.muted = true

        this.controlPlay = playerNode.find(".play")
        this.controlVol = playerNode.find(".volume")
        this.controlProgress = playerNode.find(".audio-progress")
        this.controlVolSlider = playerNode.find(".volume-slider")
        this.displayPosition = playerNode.find(".position")
        this.displayDuration = playerNode.find(".duration")
        this.controlsVolume = playerNode.find(".volume-controls")

        this.audioPlayer.on("ended", this.handleAudioFinished.bind(this))
        this.audioPlayer.on("durationchange", this.handleUpdateDuration.bind(this))
        this.audioPlayer.on("timeupdate", this.handleUpdatePosition.bind(this))
        this.audioPlayer.on("volumechange", this.handleUpdateVolume.bind(this))
        this.controlProgress.on("click", this.handleProgressClick.bind(this))
        this.controlVolSlider.on("click", this.handleVolSliderClicked.bind(this))
        this.controlPlay.on("click", this.handlePlayClicked.bind(this))
        this.controlVol.on("click", this.handleVolClicked.bind(this))
        this.controlsVolume.on("mouseenter", this.handleShowVolSlider.bind(this))
        this.controlsVolume.on("mouseleave", this.handleHideVolSlider.bind(this))
    }

    public set(source:string) {
        this.audioPlayerElement.src = source
    }

    public maskEnabled(enabled:boolean) {
        this.shouldMask = enabled
    }

    public play() {
        this.audioPlayerElement.muted = this.muted || this.shouldMask
        this.beepPlayerElement.muted = this.muted || !this.shouldMask

        if(this.audioPlayerElement.paused) {
            this.audioPlaybackPromise = this.audioPlayerElement.play()
            if(this.audioPlayerElement.currentTime < this.beepPlayerElement.duration) {
                this.beepPlayerElement.currentTime = this.audioPlayerElement.currentTime
                this.beepPlaybackPromise = this.beepPlayerElement.play()
            }
            
            this.controlPlay.children().addClass("bi-pause").removeClass("bi-play")
        }

    }

    public pause() {
        this.beepPlaybackPromise.then(() => this.beepPlayerElement.pause())
        this.audioPlaybackPromise.then(() => {
            this.audioPlayerElement.pause()
                        
            this.controlPlay.children().removeClass("bi-pause").addClass("bi-play")
        })
    }

    public setVolume(newVal:number) {
        if(newVal < 0.0 || newVal > 1.0) throw new Error("Invalid volume value " + newVal);
        this.audioPlayerElement.volume = newVal
        this.beepPlayerElement.volume = newVal
    }

    public onPlaybackFinished(callback:PlaybackFinishedCallback):Player {
        if(!this.beepPlayerElement.paused) {
            this.beepPlaybackPromise.then(() => this.beepPlayerElement.pause())
        }

        this.audioPlayer.on("ended", (e:any) => callback())
        return this
    }

    private handleAudioFinished() {
        if(!this.beepPlayerElement.paused) {
            this.beepPlaybackPromise.then(() => this.beepPlayerElement.pause())
        }
        this.controlPlay.children().removeClass("bi-pause").addClass("bi-play")
    }

    private handleUpdateDuration(e:JQuery.EventBase):void {
        // duration is in 0.00 seconds format
        const duration = (e.target as HTMLAudioElement).duration
        const seconds = (Math.floor(duration % 60) + "").padStart(2, "0")
        const minutes = (Math.floor(duration / 60) + "").padStart(1, "0")
        this.displayDuration.text(minutes + ":" + seconds)
    }

    private handleUpdatePosition(e:JQuery.EventBase):void {
        const position = (e.target as HTMLAudioElement).currentTime
        const duration = (e.target as HTMLAudioElement).duration
        
        // update display
        const seconds = (Math.floor(position % 60) + "").padStart(2, "0")
        const minutes = (Math.floor(position / 60) + "").padStart(1, "0")
        this.displayPosition.text(minutes + ":" + seconds)

        //update progress
        const progress = Math.floor(position/duration * 100)
        this.controlProgress.children().width(progress + "%")
        this.controlProgress.children().attr("aria-valuenow", progress)
    }

    private handleUpdateVolume():void {
        const volume = this.audioPlayerElement.volume
        
        const sliderValue = (volume * this.controlVolSlider.width())/this.controlVolSlider.width() * 100
        this.controlVolSlider.children().width(sliderValue + "%")
        this.controlVolSlider.children().attr("aria-valuenow", sliderValue)
    }

    private handleProgressClick(e:JQuery.ClickEvent) {
        const duration = this.audioPlayerElement.duration
        const clickPosition = e.offsetX / $(e.target).width()
        this.audioPlayerElement.currentTime = clickPosition * duration
    }

    private handleVolSliderClicked(e:JQuery.ClickEvent) {
        const clickPosition = e.offsetX / $(e.target).width()
        this.setVolume(clickPosition)
    }

    private handlePlayClicked() {
        if(this.audioPlayerElement.paused) {
            this.play()
        } else {
            this.pause()
        }
    }

    private handleVolClicked() {
        this.muted = !this.muted

        this.audioPlayerElement.muted = this.muted || this.shouldMask
        this.beepPlayerElement.muted = this.muted || !this.shouldMask

        if(this.muted) {
            this.controlVol.children("i").removeClass("bi-volume-up").addClass("bi-volume-mute")
        } else {
            this.controlVol.children("i").addClass("bi-volume-up").removeClass("bi-volume-mute")
        }
    }

    private handleShowVolSlider() {
        this.controlVolSlider.removeClass("d-none");
    }

    private handleHideVolSlider() {
        this.controlVolSlider.addClass("d-none")
    }
}